import React, { FC } from 'react';
// components
import UserConnectStatus from '../../atoms/userConnectStatus';
// style
import './index.scss';
// localization
import strings from '../../../localization';
import { footerCancelButtonVariantType, userType } from '../../../types/types';

// Renders a simple left-pointing arrow for the footer Back label.
const FooterBackIcon: FC = () => (
  <svg
    className="footer__back-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M19 12H6M6 12l6-5.5M6 12l6 5.5" />
  </svg>
);

interface FooterProps {
  handleSubmit?: any;
  submitLabel?: string;
  submitHidden?: boolean;
  isSubmitDecline?: boolean;
  handleCancel?: any;
  cancelLabel?: string;
  cancelHidden?: boolean;
  isCancelDecline?: boolean;
  disabled?: boolean;
  isConnectStatusShown?: boolean;
  isUserConnected?: boolean;
  userType?: userType;
  height?: boolean;
  isDevice?: boolean;
  connectionLabelHidden?: boolean;
  cancelButtonVariant?: footerCancelButtonVariantType;
  submitButtonVariant?: footerCancelButtonVariantType;
}

// Resolves submit button classes: decline red, chrome ghost/lighter/bordered, or primary submit.
const getSubmitButtonClassName = (
  isSubmitDecline?: boolean,
  submitButtonVariant?: footerCancelButtonVariantType,
): string => {
  if (isSubmitDecline) {
    return 'btn btn_decline';
  }
  if (submitButtonVariant) {
    return `btn btn_default btn_on-chrome--${submitButtonVariant}`;
  }
  return 'btn btn_submit';
};

// Resolves Back/cancel button classes: destructive decline, optional chrome override, or primary submit (default; same as Next).
function getCancelButtonClassName(
  isCancelDecline: boolean | undefined,
  cancelButtonVariant: footerCancelButtonVariantType | undefined,
): string {
  if (isCancelDecline) {
    return 'btn btn_decline';
  }
  if (cancelButtonVariant) {
    return `btn btn_default btn_on-chrome--${cancelButtonVariant}`;
  }
  return 'btn btn_submit';
}

// Renders bottom action bar; Back matches Next (btn_submit) unless cancelButtonVariant overrides chrome style.
const Footer: FC<FooterProps> = ({
  handleSubmit,
  submitLabel,
  submitHidden,
  isSubmitDecline,
  handleCancel,
  cancelLabel,
  isCancelDecline,
  disabled,
  cancelHidden,
  isConnectStatusShown,
  isUserConnected,
  userType,
  height,
  isDevice,
  connectionLabelHidden,
  cancelButtonVariant,
  submitButtonVariant,
}: FooterProps) => {
  const cancelButtonClassName = getCancelButtonClassName(isCancelDecline, cancelButtonVariant);
  const submitButtonClassName = getSubmitButtonClassName(isSubmitDecline, submitButtonVariant);

  return (
    <div
      className={`footer${cancelHidden ? ' footer_single-item' : ''}${height ? ' rotate' : ''}`}
      style={height ? { width: `${window.innerHeight}px` } : {}}
    >
      {!cancelHidden && (
        <div className="footer__cancel-btn">
          <button type="button" className={cancelButtonClassName} onClick={handleCancel}>
            <span className="footer__cancel-inner">
              <FooterBackIcon />
              <span>{cancelLabel ? cancelLabel : strings.footer.back}</span>
            </span>
          </button>
        </div>
      )}
      {isConnectStatusShown && userType && !isDevice && (
        <div className="footer__inner">
          <UserConnectStatus
            userType={userType}
            isUserConnected={isUserConnected ? isUserConnected : false}
            connectionLabelHidden={connectionLabelHidden}
          />
        </div>
      )}
      {!submitHidden && (
        <div className="footer__btn">
          <button type="button" className={submitButtonClassName} onClick={handleSubmit} disabled={disabled}>
            {submitLabel ? submitLabel : strings.footer.nextStep}
          </button>
        </div>
      )}
    </div>
  );
};

export default Footer;
